<template>
  <main class="mobile-page home-screen">
    <div class="mobile-screen">
      <header class="mobile-topnav">
        <a class="brand" href="/" aria-label="VitaeAI 首页">
          <span class="brand-mark" aria-hidden="true">
            <img src="/logos/vitaeai-mark.svg" alt="" />
          </span>
          <span class="brand-copy">
            <img src="/logos/vitaeai-transparent.png" alt="VitaeAI" />
            <small>AI COSMOS</small>
          </span>
        </a>
        <RouterLink class="mobile-icon-btn" to="/mobile/user" aria-label="我的">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <circle cx="12" cy="8" r="3.6" />
            <path d="M4.5 20c1.4-3.6 4.2-5.2 7.5-5.2s6.1 1.6 7.5 5.2" />
          </svg>
        </RouterLink>
      </header>

      <section class="hero">
        <span class="mobile-eyebrow">VitaeAI · 重构交互</span>
        <h1>重构人与<br /><span class="grad">数字世界</span>的<br />交互方式</h1>
        <p>每一次点击、每一次对话、每一次指令，都是人与数字世界发生关系的契机。</p>
      </section>

      <section class="orbit" aria-label="AI 数字生态示意图">
        <i class="orbit-ring r1"></i>
        <i class="orbit-ring r2"></i>
        <strong class="orbit-core">AI</strong>
        <span class="sat sat1">用户</span>
        <span class="sat sat2">品牌</span>
        <span class="sat sat3">内容</span>
        <span class="sat sat4">服务</span>
      </section>

      <div class="hero-ctas">
        <RouterLink class="mobile-btn mobile-btn-primary" :to="firstProductPath">VitaeAI 智能体</RouterLink>
        <RouterLink class="mobile-btn mobile-btn-ghost" to="/mobile/app">点指之间 APP</RouterLink>
      </div>

      <section>
        <div class="mobile-sec-head">
          <h2>理念与愿景</h2>
          <RouterLink to="/mobile/about">文化理念 ›</RouterLink>
        </div>
        <div class="mobile-hscroll">
          <article v-for="idea in ideas" :key="idea.k" class="idea-card">
            <span class="k">{{ idea.k }}</span>
            <h3>{{ idea.title }}</h3>
            <p>{{ idea.text }}</p>
          </article>
        </div>
      </section>

      <section>
        <div class="mobile-sec-head">
          <h2>生态运转场景</h2>
        </div>
        <div class="mobile-hscroll">
          <article v-for="scene in scenes" :key="scene.title" class="scene-card" :class="scene.theme">
            <span class="sc-badge">{{ scene.en }}</span>
            <strong>{{ scene.title }}</strong>
            <p>{{ scene.text }}</p>
          </article>
        </div>
      </section>

      <section>
        <div class="mobile-sec-head">
          <h2>数字生态全景</h2>
          <RouterLink to="/mobile/products">全部产品 ›</RouterLink>
        </div>
        <div v-if="productConfigLoading && !productConfigLoaded" class="product-config-state">
          正在读取官方商品目录...
        </div>
        <div v-else-if="productConfigError && !productConfigLoaded" class="product-config-state error">
          <p>{{ productConfigError }}</p>
          <button class="mobile-btn mobile-btn-ghost" type="button" @click="loadProductConfig(true)">重新加载商品信息</button>
        </div>
        <template v-else>
          <div v-if="!websiteProducts.length" class="product-config-state">
            暂无可展示商品。
          </div>
          <article
            v-for="product in mobileWebsiteProducts"
            :key="product.productCode"
            class="prod-card orderable-product-card"
          >
            <RouterLink class="product-detail-link" :to="mobileProductPath(product)">
              <span class="ph ph-employee">
                <img :src="product.image" :alt="product.title" loading="lazy" />
                <i class="ph-grid"></i>
              </span>
              <span class="pb">
                <span class="row">
                  <h3>{{ product.title }}</h3>
                  <strong class="price">{{ product.priceText }}</strong>
                </span>
                <p class="pdesc">{{ product.subtitle }}</p>
                <p class="pdesc compact">{{ product.desc }}</p>
                <span class="ptags">
                  <span
                    v-for="(item, itemIndex) in product.points"
                    :key="`${item}-${itemIndex}`"
                  >
                    {{ item }}
                  </span>
                </span>
                <span class="ptags">
                  <span v-for="method in product.allowedPaymentMethods" :key="method">
                    支持{{ paymentMethodText(method) }}
                  </span>
                </span>
                <span v-if="product.metrics.length" class="pmeta">
                  <span v-for="metric in product.metrics" :key="metric.label">
                    {{ metric.label }}：{{ metric.value }}
                  </span>
                </span>
              </span>
            </RouterLink>
            <div class="product-order-actions">
              <button
                class="mobile-btn mobile-btn-primary product-order-btn"
                type="button"
                :disabled="!canCreateOrder"
                :aria-label="`${product.title}立即下单`"
                @click="goOrder(product)"
              >
                {{ canCreateOrder ? '立即下单' : '暂未开放下单' }}
              </button>
            </div>
          </article>
        </template>
        <div class="spacer"></div>
        <RouterLink class="prod-card" to="/mobile/app">
          <span class="ph ph-app">
            <img src="/images/pexels-mobile-app-night.jpg" alt="点指之间 APP" loading="lazy" />
            <i class="ph-grid"></i>
          </span>
          <span class="pb">
            <span class="row">
              <h3>点指之间 APP</h3>
              <strong class="price violet">免费参与</strong>
            </span>
            <p class="pdesc">让每一个普通人都能公平享受数字文明带来的繁荣。内容消费、广告互动、签到分享获得活跃积分，经 AI 动态转换沉淀为能量积分。</p>
            <span class="ptags">
              <span>行为即价值</span><span>活跃→能量</span><span>生态消费</span>
            </span>
          </span>
        </RouterLink>
        <div class="spacer small"></div>
      </section>
    </div>

    <MobileTabbar active="home" />
    <MobileServiceFloating />
  </main>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import MobileTabbar from '@/components/mobile/MobileTabbar.vue'
import MobileServiceFloating from '@/components/mobile/MobileServiceFloating.vue'
import {
  paymentMethodText,
  toWebsiteProductCard,
  useWebsiteProductConfig
} from '@/composables/useWebsiteProductConfig'

const {
  productConfig,
  websiteProducts,
  productConfigLoaded,
  productConfigLoading,
  productConfigError,
  loadProductConfig
} = useWebsiteProductConfig()

const router = useRouter()

/**
 * 转换后端商品为移动端首页展示结构。
 * @returns {Array<object>} 商品卡片列表
 */
function mapWebsiteProducts() {
  return websiteProducts.value.map(product => toWebsiteProductCard(
    product,
    productConfig.value.priceHidden === true
  ))
}

const mobileWebsiteProducts = computed(mapWebsiteProducts)
const canCreateOrder = computed(() => productConfigLoaded.value
  && productConfig.value.orderEnabled === true)
const firstProductPath = computed(() => (
  websiteProducts.value[0] ? mobileProductPath(websiteProducts.value[0]) : '/mobile/products'
))

/**
 * 生成移动端商品详情路径。
 * @param product 商品对象
 * @returns {string} 详情路径
 */
function mobileProductPath(product) {
  const productCode = String(product?.productCode || '').trim()
  return productCode
    ? `/mobile/employee/${encodeURIComponent(productCode)}`
    : '/mobile/employee'
}

/**
 * 从首页商品卡片直接进入对应商品的下单页。
 * @param product 所选商品
 */
function goOrder(product) {
  if (!canCreateOrder.value || !product?.productCode) return
  router.push({ path: '/mobile/order', query: { productCode: product.productCode } })
}

const ideas = [
  { k: '理念 CONCEPT', title: '重构人与数字世界的交互方式', text: '一个触点，可以连接万千场景；一次交互，可以激活沉睡价值；一个智能体，可以重塑企业增长。' },
  { k: '愿景 VISION', title: '让AI智能体进入真实经营场景', text: '以智能为桥梁，构建出一个人人可参与、企业可增长、成果可共享的共生系统。' },
  { k: '口号 SLOGAN', title: '把每一次交互转化为可持续价值', text: '于方寸指尖的每一次交互之中，我们看见技术跨越鸿沟，看见价值回归共建者，看见每一个微小的参与终将汇聚为数字文明的浩荡长河。' }
]
const scenes = [
  { en: 'Content Engine', title: '内容创作', text: 'AI 持续产出文案与视觉素材，积累品牌内容资产', theme: 'sc-cyan' },
  { en: 'Public Traffic', title: '公域曝光', text: '智能触达连接目标用户，让内容有效连接客户', theme: 'sc-violet' },
  { en: 'Private CRM', title: '私域经营', text: '知识库问答承接咨询，个性化推荐促进转化复购', theme: 'sc-gold' }
]

onMounted(() => {
  loadProductConfig().catch(() => {})
})
</script>

<style scoped>
.home-screen { padding-bottom: calc(72px + env(safe-area-inset-bottom)); }
.brand { display: flex; align-items: center; gap: 8px; color: inherit; text-decoration: none; }
.brand-mark { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; border-radius: 9px; background: rgba(255,255,255,.05); }
.brand-mark img { width: 100%; height: 100%; display: block; }
.brand-copy img { display: block; width: 79px; max-height: 20px; object-fit: contain; }
.brand-copy small { display: block; color: var(--mobile-quiet); font-size: 8px; font-weight: 700; letter-spacing: .24em; }
.hero { padding: 18px 2px 0; }
.hero h1 { margin: 14px 0 12px; font-size: 31px; line-height: 1.26; font-weight: 900; letter-spacing: .01em; }
.hero .grad { background: linear-gradient(100deg, #8df5de 10%, #62f4d6 45%, #9b8cff 90%); -webkit-background-clip: text; background-clip: text; color: transparent; }
.hero p { padding-right: 6px; color: var(--mobile-muted); font-size: 12.5px; line-height: 1.72; }
.hero-ctas { display: flex; gap: 10px; margin-top: 18px; }
.hero-ctas .mobile-btn { flex: 1; }
.orbit { position: relative; height: 190px; margin: 20px 0px 24px; overflow: hidden; border: 1px solid rgba(98,244,214,.16); border-radius: 20px; background: radial-gradient(circle at 50% 50%, rgba(98,244,214,.14), transparent 55%), radial-gradient(circle at 78% 22%, rgba(155,140,255,.15), transparent 45%), linear-gradient(145deg, rgba(255,255,255,.05), rgba(255,255,255,.015)); }
.orbit::before { content: ""; position: absolute; inset: 14px; background-image: linear-gradient(rgba(98,244,214,.07) 1px, transparent 1px), linear-gradient(90deg, rgba(98,244,214,.07) 1px, transparent 1px); background-size: 26px 26px; mask-image: radial-gradient(circle, #000 0 40%, transparent 75%); }
.orbit-ring { position: absolute; top: 50%; left: 50%; border: 1px solid rgba(98,244,214,.22); border-radius: 50%; }
.r1 { width: 150px; height: 150px; margin: -75px 0 0 -75px; animation: spin 16s linear infinite; }
.r2 { width: 224px; height: 224px; margin: -112px 0 0 -112px; border-color: rgba(155,140,255,.2); animation: spin 26s linear infinite reverse; }
.orbit-core { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); width: 58px; height: 58px; display: flex; align-items: center; justify-content: center; border-radius: 50%; color: var(--mobile-cta-ink); font-size: 15px; font-weight: 900; background: radial-gradient(circle at 32% 28%, #8df5de, #46d6b6 60%, #2eb898); box-shadow: 0 0 34px rgba(98,244,214,.55); }
.sat { position: absolute; padding: 5px 11px; border: 1px solid var(--mobile-line-strong); border-radius: 999px; color: var(--mobile-ink); font-size: 10.5px; font-weight: 800; background: rgba(4,10,20,.85); backdrop-filter: blur(6px); box-shadow: 0 6px 18px rgba(0,0,0,.4); }
.sat1 { top: 16%; left: 12%; animation: float 5s ease-in-out infinite; }
.sat2 { top: 10%; right: 10%; animation: float 5s ease-in-out infinite 1.2s; }
.sat3 { bottom: 14%; left: 20%; animation: float 5s ease-in-out infinite .6s; }
.sat4 { bottom: 18%; right: 14%; animation: float 5s ease-in-out infinite 1.8s; }
.idea-card { flex: 0 0 208px; position: relative; overflow: hidden; padding: 15px; border: 1px solid var(--mobile-line); border-radius: 16px; background: linear-gradient(170deg, rgba(20,34,56,.55), rgba(9,18,33,.8)); }
.idea-card::after { content: ""; position: absolute; top: -24px; right: -24px; width: 76px; height: 76px; border-radius: 50%; background: radial-gradient(circle, rgba(255,207,106,.1), transparent 70%); }
.idea-card .k { color: var(--mobile-gold); font-size: 10px; font-weight: 900; letter-spacing: .14em; }
.idea-card h3 { margin: 8px 0 7px; font-size: 13.5px; line-height: 1.42; font-weight: 800; }
.idea-card p { color: var(--mobile-quiet); font-size: 10.5px; line-height: 1.68; }
.scene-card { flex: 0 0 168px; position: relative; overflow: hidden; padding: 14px; border: 1px solid var(--mobile-line); border-radius: 15px; background: linear-gradient(165deg, rgba(255,255,255,.055), rgba(255,255,255,.015)); }
.scene-card::before { content: ""; position: absolute; right: 0; bottom: 0; left: 0; height: 2.5px; border-radius: 2px; }
.scene-card .sc-badge { position: absolute; top: 11px; right: 11px; color: var(--mobile-faint); font-size: 8px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
.scene-card strong { display: block; margin: 6px 0 7px; font-size: 15px; font-weight: 900; }
.scene-card p { position: relative; z-index: 1; color: var(--mobile-quiet); font-size: 10px; line-height: 1.62; }
.sc-cyan::before { background: linear-gradient(90deg, var(--mobile-cyan), transparent); }
.sc-cyan strong { color: #a8f7e4; }
.sc-violet::before { background: linear-gradient(90deg, var(--mobile-violet), transparent); }
.sc-violet strong { color: #c3bcff; }
.sc-gold::before { background: linear-gradient(90deg, var(--mobile-gold), transparent); }
.sc-gold strong { color: #ffe0a8; }
.spacer { height: 14px; }
.spacer.small { height: 8px; }
.prod-card { position: relative; overflow: hidden; border: 1px solid var(--mobile-line); border-radius: 18px; color: inherit; text-decoration: none; background: linear-gradient(170deg, rgba(16,28,48,.6), rgba(8,16,30,.82)); box-shadow: 0 10px 32px rgba(0,0,0,.3); transition: transform .18s ease, border-color .18s ease; }
.prod-card:active { transform: scale(.98); border-color: rgba(98,244,214,.45); }
.orderable-product-card .product-detail-link { display: block; color: inherit; text-decoration: none; }
.orderable-product-card .pb { padding-bottom: 14px; }
.product-order-actions { display: flex; justify-content: flex-end; padding: 0 16px 16px; }
.product-order-btn { min-width: 132px; min-height: 40px; padding: 0 14px; font-size: 13.5px; }
.ph { height: 128px; position: relative; display: flex; align-items: center; justify-content: center; overflow: hidden; }
.ph img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.ph::after { content:""; position:absolute; inset:0; background:linear-gradient(180deg,rgba(2,5,13,.18),rgba(2,5,13,.72)); }
.ph-employee img { filter: saturate(1.08) contrast(1.04); }
.ph-app img { filter: saturate(1.05); }
.ph-employee { background: radial-gradient(140px 90px at 50% 110%, rgba(98,244,214,.35), transparent 70%), linear-gradient(150deg, #0d2137, #0a1830 60%, #071021); }
.ph-app { background: radial-gradient(140px 90px at 50% 110%, rgba(155,140,255,.32), transparent 70%), linear-gradient(150deg, #1b1836, #12102a 60%, #0a0918); }
.ph-grid { position: absolute; inset: 0; background-image: linear-gradient(rgba(222,246,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(222,246,255,.05) 1px, transparent 1px); background-size: 22px 22px; mask-image: linear-gradient(180deg, transparent, #000 60%); }
.ph-robot { position: relative; z-index: 2; width: 56px; height: 56px; display: flex; align-items: center; justify-content: center; border: 1px solid rgba(98,244,214,.4); border-radius: 16px; background: rgba(4,12,22,.75); box-shadow: 0 0 26px rgba(98,244,214,.35); }
.ph-phone { position: relative; z-index: 2; width: 34px; height: 56px; border: 1.5px solid rgba(155,140,255,.55); border-radius: 9px; background: rgba(4,12,22,.8); box-shadow: 0 0 26px rgba(155,140,255,.4); }
.pb { display: block; padding: 14px 16px 16px; }
.pb .row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.pb h3 { font-size: 15.5px; font-weight: 800; }
.price { color: var(--mobile-cyan); font-size: 16px; font-weight: 900; white-space: nowrap; }
.price.violet { color: var(--mobile-violet); }
.pdesc { margin-top: 7px; color: var(--mobile-quiet); font-size: 11px; line-height: 1.72; display: -webkit-box; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 4; }
.pdesc.compact { margin-top: 6px; }
.ptags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 11px; }
.ptags span { padding: 3px 8px; border: 1px solid var(--mobile-line-strong); border-radius: 6px; color: var(--mobile-muted); font-size: 10px; font-weight: 700; background: rgba(255,255,255,.03); }
.ptags span:nth-child(1) { color: #a8f7e4; border-color: rgba(98,244,214,.3); background: rgba(98,244,214,.07); }
.pmeta { display: flex; flex-wrap: wrap; gap: 6px 12px; margin-top: 10px; color: var(--mobile-faint); font-size: 10px; }
.product-config-state { display: grid; gap: 12px; justify-items: center; padding: 15px; border: 1px solid var(--mobile-line); border-radius: 15px; color: var(--mobile-muted); font-size: 12px; text-align: center; background: rgba(255,255,255,.03); }
.product-config-state.error { color: #ff9c9c; }
.product-config-state .mobile-btn { width: 100%; }
@keyframes spin { to { transform: rotate(360deg); } }
@keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }
@media (min-width: 700px) { .home-screen { max-width: 540px; margin: 0 auto; } }
</style>
